import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';

/**
 * Union of everything this service is willing to carry.
 * Extend/narrow this if you want stricter typing app-wide.
 */
export type ListenerPayload =
  | string
  | number
  | boolean
  | object
  | unknown[]
  | null
  | undefined;

export interface ListenerEvent<T = ListenerPayload> {
  key: string;
  value: T;
  timestamp: number;
}

/**
 * General purpose, app-wide listening (pub/sub) service.
 *
 * - Works with ANY data type: string, number, boolean, object, array.
 * - Components/services emit under a "key" (channel name) and
 *   listeners subscribe to that same key.
 * - Because it's `providedIn: 'root'`, one singleton instance is
 *   shared across the whole app — no need to pass it through inputs
 *   or wire up parent/child @Output chains.
 *
 * Usage:
 *   this.listener.emit('user-updated', { id: 1, name: 'Ada' });
 *   this.listener.on<User>('user-updated').subscribe(user => ...);
 */
@Injectable({
  providedIn: 'root',
})
export class ListenerService {
  private readonly bus = new Subject<ListenerEvent>();

  /** Keeps the most recent value per key so late subscribers can peek at it synchronously. */
  private readonly lastValues = new Map<string, ListenerEvent>();

  /**
   * Emit a value on a given channel/key.
   * T is inferred from whatever you pass in, or can be set explicitly:
   *   emit<number>('counter', 5)
   *   emit('flag', true)
   *   emit('user', { id: 1 })
   *   emit('list', [1, 2, 3])
   */
  emit<T = ListenerPayload>(key: string, value: T): void {
    const event: ListenerEvent<T> = { key, value, timestamp: Date.now() };
    this.lastValues.set(key, event as ListenerEvent);
    this.bus.next(event as ListenerEvent);
  }

  /**
   * Subscribe to a specific key. Cast the return type via the generic
   * to get strong typing at the call site:
   *   this.listener.on<boolean>('flag').subscribe(v => ...)
   */
  on<T = ListenerPayload>(key: string): Observable<T> {
    return this.bus.asObservable().pipe(
      filter((event) => event.key === key),
      map((event) => event.value as T)
    );
  }

  /** Subscribe to a specific key, only once. */
  onOnce<T = ListenerPayload>(key: string): Observable<T> {
    return new Observable<T>((subscriber) => {
      const sub = this.on<T>(key).subscribe((value) => {
        subscriber.next(value);
        subscriber.complete();
        sub.unsubscribe();
      });
      return () => sub.unsubscribe();
    });
  }

  /** Subscribe to every event on every key (useful for logging/debugging). */
  onAny(): Observable<ListenerEvent> {
    return this.bus.asObservable();
  }

  /** Synchronously read the last value emitted on a key, if any. */
  getLast<T = ListenerPayload>(key: string): T | undefined {
    return this.lastValues.get(key)?.value as T | undefined;
  }

  /** Remove the cached last-value for a key (does not affect live subscribers). */
  clear(key: string): void {
    this.lastValues.delete(key);
  }

  /** Remove all cached last-values. */
  clearAll(): void {
    this.lastValues.clear();
  }
}